import { idiv, isqrt, tileCenter, unitsToTile } from '../core/fixed';
import type { Path, Unit, WorldPoint } from '../entities/unit';
import { isBlocked, type GameMap } from '../map/gameMap';
import type { Pathfinder, TilePoint } from '../map/pathfinding';
import type { OccupancyFn } from '../map/unitGrid';

export interface PlanOptions {
  /** Standing units to route around. Omit to ignore other units entirely. */
  readonly occupancy?: OccupancyFn | undefined;
  /**
   * Which end tiles count as "got there". Default: the goal tile itself, or any tile
   * touching it when the goal tile is occupied. Gathering and building pass "adjacent
   * to the footprint".
   */
  readonly accept?: ((tx: number, ty: number) => boolean) | undefined;
}

/**
 * Plan a path from the unit to (targetX, targetY) in world units.
 *
 * Standing units are treated as obstacles first. If that leaves no acceptable end tile
 * (a standing unit plugs the only corridor), the path is re-planned ignoring them and the
 * unit walks through: detours win when they exist, and nothing can ever deadlock.
 * Returns null when the unit is already as close as it can get.
 */
export function planPath(
  unit: Unit,
  map: GameMap,
  pathfinder: Pathfinder,
  targetX: number,
  targetY: number,
  options: PlanOptions = {},
): Path | null {
  const startTx = unitsToTile(unit.x);
  const startTy = unitsToTile(unit.y);
  const goalTx = Math.max(0, Math.min(map.width - 1, unitsToTile(targetX)));
  const goalTy = Math.max(0, Math.min(map.height - 1, unitsToTile(targetY)));
  const goalOpen = !isBlocked(map, goalTx, goalTy);
  const { occupancy } = options;
  const accept =
    options.accept ??
    ((tx: number, ty: number) => {
      if (tx === goalTx && ty === goalTy) return true;
      const goalTaken = occupancy !== undefined && occupancy(goalTx, goalTy);
      return goalTaken && Math.max(Math.abs(tx - goalTx), Math.abs(ty - goalTy)) === 1;
    });

  const endOf = (tiles: TilePoint[]): TilePoint =>
    tiles[tiles.length - 1] ?? { x: startTx, y: startTy };

  const here = { x: unit.x, y: unit.y };
  const area = map.width * map.height;
  let tiles = pathfinder.findPath(startTx, startTy, goalTx, goalTy, occupancy, area, here);
  let ignoresOccupancy = false;
  if (occupancy && !accept(endOf(tiles).x, endOf(tiles).y)) {
    const through = pathfinder.findPath(startTx, startTy, goalTx, goalTy, undefined, area, here);
    if (accept(endOf(through).x, endOf(through).y)) {
      tiles = through;
      ignoresOccupancy = true;
    }
  }

  const waypoints: WorldPoint[] = tiles.map((t) => ({ x: tileCenter(t.x), y: tileCenter(t.y) }));
  const end = endOf(tiles);
  if (end.x === goalTx && end.y === goalTy && goalOpen) {
    // Finish at the exact requested position, not the tile center.
    waypoints.pop();
    waypoints.push({ x: targetX, y: targetY });
  }

  const final = waypoints[waypoints.length - 1];
  if (!final || (final.x === unit.x && final.y === unit.y)) return null;
  return { waypoints, index: 0, targetX, targetY, ignoresOccupancy };
}

/**
 * Advance the unit along its path, spending up to `speed` units this tick across
 * waypoints. If the next position lands on a tile that has become blocked, or on a
 * standing unit (unless this path deliberately walks through), re-plan once per tick; if
 * the way is still blocked, snap to the tile center and stop for this tick. When the path
 * completes it becomes null.
 */
export function stepMovement(
  unit: Unit,
  map: GameMap,
  pathfinder: Pathfinder,
  occupancy?: OccupancyFn,
): void {
  let budget = unit.def.speed;
  let replanned = false;
  while (budget > 0 && unit.path) {
    const path = unit.path;
    const wp = path.waypoints[path.index];
    if (!wp) {
      unit.path = null;
      return;
    }
    const dx = wp.x - unit.x;
    const dy = wp.y - unit.y;
    const dist = isqrt(dx * dx + dy * dy);

    let nx: number;
    let ny: number;
    let spent: number;
    if (dist <= budget) {
      nx = wp.x;
      ny = wp.y;
      spent = dist;
    } else {
      nx = unit.x + idiv(dx * budget, dist);
      ny = unit.y + idiv(dy * budget, dist);
      spent = budget;
    }

    const ntx = unitsToTile(nx);
    const nty = unitsToTile(ny);
    const enteringNewTile = ntx !== unitsToTile(unit.x) || nty !== unitsToTile(unit.y);
    const standingAhead =
      enteringNewTile && !path.ignoresOccupancy && occupancy !== undefined && occupancy(ntx, nty);
    if (isBlocked(map, ntx, nty) || standingAhead) {
      if (replanned) {
        // Still blocked after re-planning: snap to this tile's center (always a legal spot) and
        // drop the path; whatever the unit is doing re-plans from there next tick. Never spin.
        unit.x = tileCenter(unitsToTile(unit.x));
        unit.y = tileCenter(unitsToTile(unit.y));
        unit.path = null;
        return;
      }
      replanned = true;
      unit.path = planPath(unit, map, pathfinder, path.targetX, path.targetY, { occupancy });
      if (!unit.path) return;
      continue; // spend the remaining budget on the new path
    }

    unit.x = nx;
    unit.y = ny;
    budget -= Math.max(spent, 1); // a zero-length hop must still consume budget
    if (nx === wp.x && ny === wp.y) {
      path.index++;
      if (path.index >= path.waypoints.length) unit.path = null;
    }
  }
}
